import {
  forwardRef,
  ForwardedRef,
  HTMLAttributes,
  ReactNode,
  RefObject,
  useContext,
} from "react";
import { DismissButton, useOverlay } from "@react-aria/overlays";
import { FocusScope } from "@react-aria/focus";
import styled, { ThemeContext } from "styled-components";
import { PositionProps, SpaceProps, space, position } from "styled-system";

import Card from "../Card/Card";
import { PopoverProps } from "./Popover";

const PopoverContainer = styled(Card)<SpaceProps & PositionProps>`
  margin: 0;
  position: absolute;
  max-width: 335px;
  ${space}
  ${position}
`;

interface ContentProps
  extends HTMLAttributes<HTMLElement>,
    Partial<PopoverProps> {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  triggerRef: RefObject<HTMLElement>;
}

const Content = forwardRef(
  (
    {
      children,
      isOpen,
      onClose,
      placement = "bottom left",
      offsetY = 14,
      offsetX = 0,
      triggerRef,
      ...otherProps
    }: ContentProps,
    ref: ForwardedRef<HTMLElement>
  ) => {
    const themeContext = useContext(ThemeContext);
    const popoverShadow = `box-shadow: ${themeContext.shadows["hover"]}`;
    const placementX =
      placement.split(" ")[1] === "left"
        ? { right: offsetX }
        : { left: offsetX };

    const { overlayProps } = useOverlay(
      {
        onClose,
        isOpen,
        isDismissable: true,
        shouldCloseOnBlur: true,
        shouldCloseOnInteractOutside: (element: Element) => {
          if (element) {
            if (triggerRef.current === element) {
              return false;
            }
            const contentRef = ref as RefObject<HTMLElement>;
            return (contentRef &&
              contentRef.current &&
              !contentRef.current.contains(element)) as boolean;
          }
          return false;
        },
      },
      ref as RefObject<HTMLElement>
    );

    return (
      <FocusScope restoreFocus autoFocus contain>
        <PopoverContainer
          {...overlayProps}
          {...otherProps}
          {...placementX}
          ref={ref}
          marginTop={offsetY}
          css={popoverShadow}
        >
          <DismissButton onDismiss={onClose} />
          {children}
          <DismissButton onDismiss={onClose} />
        </PopoverContainer>
      </FocusScope>
    );
  }
);

export default Content;
